import axios, { type AxiosRequestConfig, type AxiosError, type AxiosResponse } from "axios";
import { nanoid } from 'nanoid';

import { ejectPromise, wait } from '@livelybone/promise-wait';


import { toast } from "sonner";
import type { Result } from "#/api";
import { getErrorMsg } from '@/utils/fns';
import { AuthToken } from "@/store/userStore";

export function logout() {
	AuthToken.remove();
	location.href = location.origin + '/#/auth/login'
}

class HttpTasksManager {
  /**
   * 当前请求队列
   * - isBlock 表示请求与其他请求互斥，同一时间不能存在另一个正在连接的请求
   * */
  httpTasks: {
    id: string;
    runner: () => Promise<any>;
    isBlock?: boolean;
    blockTimeAfterRun?: number;
  }[] = [];

  async addTask(
    id: string,
    runner: () => Promise<any>,
    isBlock?: boolean,
    blockTimeAfterRun?: number,
  ) {
    if (isBlock || this.httpTasks.some(it => it.isBlock)) {
      while (this.httpTasks.length) {
        const task = this.httpTasks.shift();
        await task?.runner();
        if (task?.isBlock && task.blockTimeAfterRun) {
          await wait(task.blockTimeAfterRun);
        }
      }
    }
    this.httpTasks.push({ id, runner, isBlock, blockTimeAfterRun });
  }
}

const tasksManager = new HttpTasksManager();

/** 异常处理程序 */
const errorHandler = (error: AxiosError<any>) => {
  const { config, response, request } = error;
  const showNotification = (config as any)?.showNotification;
  const url = config?.url || request?.options?.url || request?.url;
  const status = response?.status;
  const code = response?.data?.code;
  const statusText = response?.statusText;
  const message = !status
    ? error.message || 'Network connection failed...'
    : response?.data?.message || error.message;
  Object.assign(error, {
    url,
    status,
    code,
    statusText,
    params: JSON.stringify(config?.params),
    data: JSON.stringify(config?.data),
    message,
  });
  if (!response?.data.succ || (response?.data.code !== 0 && response?.data.code !== 200)) {
    const error = new Error(getErrorMsg(response?.data) || 'Unknown error');
    
    toast.error(error.message);
  }
  const notice = async (params: any) => {
    console.warn('Api', error, { ...error }, params);
    if (!showNotification) return null;
    return toast.error(params);
  };
  if (!url) return Promise.reject(error);
  if (status) {
    if (
      !url.includes('login') &&
      !location.href.includes('reset-password') &&
      ([401, 403].includes(status) || message.includes('Authentication failed'))
    ) {
      logout();
    }
    return Promise.reject(error);
  }
  return Promise.reject(error);
};

// 创建 axios 实例
const axiosInstance = axios.create({
	baseURL: import.meta.env.VITE_APP_BASE_API,
	timeout: 50000,
	headers: { "Content-Type": "application/json;charset=utf-8" },
});

// 请求拦截
axiosInstance.interceptors.request.use(async (options: any) => {
  const id = nanoid();
  const taskResult = ejectPromise();
  await tasksManager.addTask(
    id,
    () => taskResult,
    !!options.block,
    options.blockTimeAfterResponse,
  );
  // 上面这段代码用于管理 http 请求队列，目前就是针对 token 刷新时老 token 失效问题设计的解决方案

  return {
    ...options,
    id,
    afterResponse: taskResult.resolve,
    params: { ...options.params, _t: Date.now() },
    headers: {
      ...options.headers,
      Authorization: `Bearer ${AuthToken.get()?.replace(/"/g, '') || ''}`,
    },
  };
});

// 响应拦截
axiosInstance.interceptors.response.use(async res => {
  const response: any = ('response' in res && res.response) || res;
  const options: any = response.config;
  options.afterResponse?.();
  const { data } = response;
  if (data instanceof Blob) return data;
  if (!data.succ || (data.code !== 0 && data.code !== 200)) {
    const error = new Error(getErrorMsg(data) || 'Unknown error');

    const $message = data.message.toLowerCase();

    if (data.message == 'FORCE_CHANGE_PASSWORD' || ($message.includes('change') && $message.includes('password'))) {
      location.href = location.origin + '/#/auth/reset-password';
      return;
    }
    

    Object.assign(error, {
      params: JSON.stringify(options.params),
      data: JSON.stringify(options.data),
      code: data.code,
      response: {
        ...response,
        status: response.status,
        statusText: response.statusText,
        data,
      },
      request: {
        options,
        url: [options.prefix, options.url].filter(Boolean).join(''),
      },
    });
    throw error;
  }
	
  return { ...response.data, response: response };
});

axiosInstance.interceptors.response.use(undefined, error => {
  error.config?.afterResponse?.();
  return errorHandler(error).catch(err => {
    if (err.status) {
      const msg = String(getErrorMsg(err)).replace(
        new RegExp(`^(${err.url}\\s*)?`),
        err.url + ' ',
      );
      const fetchErr = new Error(
        `Failed to fetch (status: ${err.status}, code: ${err.code}): ${msg}`,
      );
      fetchErr.cause = err;
    }
    return Promise.reject(err);
  });
});

class APIClient {
	get<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "GET" });
	}

	post<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "POST" });
	}

	put<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "PUT" });
	}

	delete<T = any>(config: AxiosRequestConfig): Promise<T> {
		return this.request({ ...config, method: "DELETE" });
	}

	request<T = any>(config: AxiosRequestConfig): Promise<T> {
		return new Promise((resolve, reject) => {
			axiosInstance
				.request<any, AxiosResponse<Result>>(config)
				.then((res: AxiosResponse<Result>) => {
					resolve(res as unknown as Promise<T>);
				})
				.catch((e: Error | AxiosError) => {
					reject(e);
				});
		});
	}
}
export default new APIClient();
